import React from 'react'
import { Button, makeStyles, tokens, mergeClasses, ProgressBar } from '@fluentui/react-components'
import { ModalActions } from '../../common/modal/Modal'
import { t } from '../../../i18n/i18n'
import { useContext, useEffect, useState } from 'react'
import { DataContext } from '../../../context/DataContext'
import { DataClassification, DemandAnalysis, DemandAnalysisProcessOwner, SubscriptionLevel } from '../../../api/schemas/schema'
import { Text } from '../../common/text/Text'
import { ControlledDatePicker } from '../../common/inputs/DatePicker'
import { useForm } from 'react-hook-form'
import { api } from '../../../helpers/apiHelper'
import { DemandAnalysisProcessOwnerPicker } from '../../compliance/DemandAnalysisProcessOwnerPicker'
import { ControlledAccountPicker } from '../../common/inputs/ControlledAccountPicker'
import { handleMessage } from '../../../helpers/stateHelper'
import { enumValuesToOptions, translateEnumValue } from '../../../helpers/enumHelper'
import { ControlledTextInput, TextInput } from '../../common/inputs/TextInput'
import { IItemState } from '../../../interfaces/IItemState'
import { ControlledDropDown } from '../../common/inputs/ControlledDropdown'
import { formatDate } from '../../../helpers/stringHelper'
import { DraggableModal } from '../../common/modal/DraggableModal'
import { Form } from '../../common/form/Form'
import { Grid } from '../../common/grid/Grid'
import { GridItem } from '../../common/grid/GridItem'

const useStyles = makeStyles({
   textWrapper: {
      '> p:last-child': {
         marginTop: tokens.spacingVerticalXS,
      },
      '> p:first-child': {
         marginBottom: 0,
      },
   },
   marginTop: {
      marginTop: tokens.spacingVerticalM,
   },
})
interface ModalEditDemandInformationProps {
   analysisState: IItemState<DemandAnalysis>
   isOpen: boolean
   closeModal: () => void
   setAnalysisState: React.Dispatch<React.SetStateAction<IItemState<DemandAnalysis>>>
}

export const ModalEditDemandInformation = ({ analysisState, isOpen, closeModal, setAnalysisState }: ModalEditDemandInformationProps) => {
   const dataContext = useContext(DataContext)
   const { hasMsalToken, userAccount } = dataContext.state
   const { setRootState } = dataContext
   const classes = useStyles()
   const [processes, setProcesses] = useState([])
   const demandAssessmentForm = useForm<DemandAnalysis>({ disabled: analysisState.disabled })
   const {
      control,
      reset,
      getValues,
      setValue,
      trigger,
      handleSubmit,
      watch,
      formState: { isValid, isSubmitting, isDirty },
   } = demandAssessmentForm
   const analysis = analysisState.item

   useEffect(() => {
      try {
         const fetchProcesses = async () => {
            const response = (await api().getAllProcesss()).data
            setProcesses(response)
         }
         fetchProcesses()
      } catch (error) {
         handleMessage(setRootState, 'error', t('SomethingWentWrong'), t('CouldNotFetchItem'))
      }
   }, [hasMsalToken, setRootState])

   useEffect(() => {
      if (isOpen) {
         reset(analysis)
      }
   }, [isOpen, analysis, reset])

   const onSubmit = async (data: DemandAnalysis) => {
      trigger()
      if (isValid) {
         try {
            const updatedData = {
               ...data,
               modifiedById: userAccount.id,
            }

            const updatedDemandAnalysis: DemandAnalysis = (await api().updateDemandAnalysis(updatedData)).data

            setAnalysisState((prevState) => ({
               ...prevState,
               item: updatedDemandAnalysis,
            }))

            reset(updatedDemandAnalysis, { keepIsSubmitSuccessful: true, keepIsSubmitted: true })
            closeModal()
            handleMessage(setRootState, 'success', t('ItemSuccessfullyUpdated', { Item: t('DemandAssessment') }))
         } catch (error: any) {
            let errorMessage = t('SomethingWentWrong')
            handleMessage(setRootState, 'error', errorMessage, t('CouldNotUpdateDemandAssessment'))
         }
      }
   }

   return (
      <>
         <DraggableModal title={t('EditDemandAssessment')} open={analysis && isOpen} onClose={closeModal} noDivider={isSubmitting}>
            {isSubmitting ? (
               <>
                  <ProgressBar />
                  <Text>{t('ThisCanTakeAMoment')}</Text>
               </>
            ) : (
               <Form onSubmit={handleSubmit(onSubmit)}>
                  <Grid>
                     <GridItem size="12/12">
                        <ControlledTextInput required control={control} name={'name'} label={t('AssessmentName')} />
                     </GridItem>
                     <GridItem size="6/12">
                        <TextInput
                           required
                           value={formatDate(analysis.analysisStart)}
                           label={t('StartOfAssessment')}
                           tooltipMsg={t('ValueCannotBeChanged')}
                           showTooltip
                           disabled
                        />
                     </GridItem>
                     <GridItem size="6/12">
                        <ControlledDatePicker
                           required
                           control={control}
                           name={'analysisEnd'}
                           label={t('EndOfAssessment')}
                           compareWith={'analysisStart'}
                           getValues={getValues}
                        />
                     </GridItem>
                     <GridItem size="12/12">
                        <ControlledAccountPicker required label={t('Owner')} control={control} name={'analysisOwner'} watch={watch} setValue={setValue} />
                     </GridItem>
                     <GridItem size="12/12">
                        <TextInput
                           required
                           value={translateEnumValue(SubscriptionLevel, analysis.demandLevel)}
                           label={t('DemandLevel')}
                           tooltipMsg={t('ValueCannotBeChanged')}
                           showTooltip
                           disabled
                        />
                     </GridItem>
                     <GridItem size="12/12">
                        <TextInput
                           required
                           value={analysis.workplace.name}
                           label={t('Workplace')}
                           tooltipMsg={t('ValueCannotBeChanged')}
                           showTooltip
                           disabled
                        />
                     </GridItem>

                     <GridItem size="12/12">
                        <ControlledDropDown
                           required
                           control={control}
                           name={'dataClassification'}
                           label={t('InformationManagement')}
                           options={enumValuesToOptions(DataClassification)}
                        />
                     </GridItem>
                     {analysis.processes.length > 0 && (
                        <GridItem size="12/12">
                           <section className={mergeClasses(classes.textWrapper, classes.marginTop)}>
                              <Text>{t('ProcessOwner')}</Text>
                              {analysis.demandAnalysisProcessOwners.map((processOwner: DemandAnalysisProcessOwner, index) => (
                                 <DemandAnalysisProcessOwnerPicker
                                    key={index}
                                    demandAssessmentForm={demandAssessmentForm}
                                    processes={processes}
                                    process={processOwner.process.text}
                                 ></DemandAnalysisProcessOwnerPicker>
                              ))}
                           </section>
                        </GridItem>
                     )}
                     <GridItem size="12/12">
                        <ControlledTextInput control={control} name={'comment'} label={t('Comment')} />
                     </GridItem>
                     <GridItem size="12/12">
                        <ModalActions className={classes.marginTop}>
                           <Button onClick={closeModal} disabled={isSubmitting}>
                              {analysisState.disabled ? t('Close') : t('Cancel')}
                           </Button>
                           {!analysisState.disabled && (
                              <Button appearance="primary" type="submit" disabled={isSubmitting || !isDirty}>
                                 {t('Save')}
                              </Button>
                           )}
                        </ModalActions>
                     </GridItem>
                  </Grid>
               </Form>
            )}
         </DraggableModal>
      </>
   )
}
